import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Text,
    VStack,
    Checkbox,
    RadioGroup,
    Radio,
    FormLabel,
    FormControl,
    Box,
    HStack,
    Textarea,
    useToast,
    Input,
    IconButton,
    Icon,
    Alert,
    AlertIcon,
    Tooltip,
    Spinner,
    Flex,
    Tag,
    Wrap,
    WrapItem,
    Badge,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverArrow,
    PopoverFooter,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import PartType from '../components/PartType';
import SourceBadge, { getSourceLabel } from '../components/SourceBadge';
import { useRequestQuotes } from '../hooks/useRequestQuotes';
import { useSkipQuoting } from '../hooks/useSkipQuoting';
import { useUpdateOrderDetails } from '../hooks/useUpdateOrderDetails';
import { useGetQuoteData } from '../hooks/useGetQuoteData';
import { SupplierSearchField } from '../components/SupplierSearchField';
import { TbPencil, TbInfoCircle } from 'react-icons/tb';
import SingleDateCalendar from '../components/SingleDateCalendar';
import { TbCalendar } from 'react-icons/tb';
import { useIsOverflowing } from '../hooks/useIsOverflowing';

const VENDOR_MESSAGE_QUICK_SELECT = [
    "Local delivery and vendor-managed returns required. No freight/shipping accepted",
];

const SKIP_QUOTE_MARKETPLACES = ['opstrax', 'ccc', 'tesla'] as const;

const getSkipQuotingMarketplaces = (marketplaces: Array<string>) =>
    SKIP_QUOTE_MARKETPLACES.filter((key) => marketplaces.includes(key));

const formatSkipQuotingSources = (marketplaces: Array<string>): string => {
    const names = getSkipQuotingMarketplaces(marketplaces).map(getSourceLabel);
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};


const formatScheduledDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

// Mirrors the API's business-day delivery math (skipWeekendsLateNights):
// only Mon-Fri count toward the delivery window
const addBusinessHours = (start: Date, hours: number): Date => {
    const result = new Date(start);
    let fullDays = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    while (fullDays > 0) {
        result.setDate(result.getDate() + 1);
        if (result.getDay() !== 0 && result.getDay() !== 6) fullDays--;
    }

    result.setTime(result.getTime() + remainingHours * 60 * 60 * 1000);

    const dayOfWeek = result.getDay();
    if (dayOfWeek === 0) {
        result.setDate(result.getDate() + 1);
    } else if (dayOfWeek === 6) {
        result.setDate(result.getDate() + 2);
    }

    return result;
};

interface SubmitQuoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string;
    resubmit?: boolean;
}

const SubmitQuoteModal = ({ isOpen, onClose, orderId, resubmit }: SubmitQuoteModalProps) => {
    const [potentialTotalLoss, setPotentialTotalLoss] = useState(false);
    const [oemEndorsement, setOemEndorsement] = useState(false);
    const [expedited, setExpedited] = useState(false);
    const [quotesOnly, setQuotesOnly] = useState(false);
    const [dealerReferral, setDealerReferral] = useState(false);
    const [selectedDealer, setSelectedDealer] = useState<{ _id: string; title: string; } | null>(null);
    const [allowAutoOrder, setAllowAutoOrder] = useState(true);
    const [carStatus, setCarStatus] = useState('');
    const [vendorMessage, setVendorMessage] = useState('');
    const [showQuickSelect, setShowQuickSelect] = useState(false);
    const [isEditingRoNumber, setIsEditingRoNumber] = useState(false);
    const [editedRoNumber, setEditedRoNumber] = useState('');
    const initializedRoForOpen = useRef(false);
    const [hasWeldedOrVinCoded, setHasWeldedOrVinCoded] = useState<'yes' | 'no' | ''>('');
    const [scheduledArrivalDate, setScheduledArrivalDate] = useState<Date | null>(null);
    const [wantsSchedule, setWantsSchedule] = useState<'yes' | 'no' | ''>('');
    const [showDatePicker, setShowDatePicker] = useState(false);
    const requestQuotesMutation = useRequestQuotes();
    const skipQuotingMutation = useSkipQuoting();
    const updateOrderDetailsMutation = useUpdateOrderDetails();
    const queryClient = useQueryClient();
    const toast = useToast();

    const { data: quoteData, isLoading } = useGetQuoteData(isOpen ? orderId : null, !!resubmit);
    const autoOrderBlockedForCustomerPay = !!quoteData?.disableAutoOrderCustomerPay && !!quoteData?.isCustomerPayOrder;
    const [bodyRef, bodyOverflows] = useIsOverflowing(isOpen);

    // Auto-order is derived, never latched into state: any of these conditions
    // forces it off, and clearing them restores it. `allowAutoOrder` state only
    // holds the explicit opt-out of users who can see the checkbox.
    const autoOrderForcedOff = !quoteData?.orgAllowAutoOrder
        || !!quoteData?.oemCertification
        || autoOrderBlockedForCustomerPay
        || potentialTotalLoss
        || quotesOnly;
    const effectiveAllowAutoOrder = !autoOrderForcedOff
        && (quoteData?.canDisableAutoOrder ? allowAutoOrder : true);

    const handleQuickSelect = (message: string) => {
        setVendorMessage(prev => (prev.trim() ? `${prev}\n${message}` : message));
    };

    useEffect(() => {
        if (quoteData?.oemCertification && !quotesOnly) {
            setQuotesOnly(true);
        }
    }, [quoteData?.oemCertification, quotesOnly]);

    useEffect(() => {
        if (quoteData?.enforceOemEndorsement && !oemEndorsement) {
            setOemEndorsement(true);
        }
    }, [quoteData?.enforceOemEndorsement, oemEndorsement]);

    useEffect(() => {
        if (!isOpen) {
            setPotentialTotalLoss(false);
            setOemEndorsement(false);
            setExpedited(false);
            setQuotesOnly(false);
            setAllowAutoOrder(true);
            setCarStatus('');
            setHasWeldedOrVinCoded('');
            setDealerReferral(false);
            setSelectedDealer(null);
            setVendorMessage('');
            setShowQuickSelect(false);
            setScheduledArrivalDate(null);
            setWantsSchedule('');
            setShowDatePicker(false);
            initializedRoForOpen.current = false;
            setIsEditingRoNumber(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !quoteData) return;
        if (!initializedRoForOpen.current) {
            initializedRoForOpen.current = true;
            setEditedRoNumber(quoteData.roNumber);
            setIsEditingRoNumber(quoteData.roNumber.length === 0);
            return;
        }
        if (!isEditingRoNumber) {
            setEditedRoNumber(quoteData.roNumber);
        }
    }, [isOpen, quoteData, isEditingRoNumber]);

    const handleRoNumberEdit = () => {
        setIsEditingRoNumber(true);
    };

    const handleRoNumberSave = async () => {
        if (quoteData && editedRoNumber.trim() !== quoteData.roNumber) {
            updateOrderDetailsMutation.mutate({
                orderId,
                roNumber: editedRoNumber.trim()
            });
        }
        setIsEditingRoNumber(false);
    };

    const isQuoteActionBlocked = !carStatus
        || !editedRoNumber.trim().length
        || (dealerReferral && !selectedDealer)
        || (quoteData?.needsPartTypeVerification && hasWeldedOrVinCoded === 'yes' && !quoteData?.parts.some(part => part.manualFlag));
    const skipQuotingMarketplaces = getSkipQuotingMarketplaces(quoteData?.marketplaces ?? []);
    const areQuoteActionsDisabled = isQuoteActionBlocked
        || quoteData?.canQuote === false
        || skipQuotingMutation.isPending
        || requestQuotesMutation.isPending;
    const skipQuoteDisabled = isQuoteActionBlocked
        || skipQuotingMutation.isPending
        || requestQuotesMutation.isPending;

    const handleSubmitQuote = async () => {
        if (areQuoteActionsDisabled || !quoteData) {
            return;
        }
        requestQuotesMutation.mutateAsync({
            orderId: quoteData.orderId,
            potentialTotal: potentialTotalLoss,
            preOrder: carStatus === 'pre-order',
            oemEndorsement: oemEndorsement,
            quotesOnly: quotesOnly,
            allowAutoOrder: effectiveAllowAutoOrder,
            dealerReferral: selectedDealer?._id,
            vendorMessage: vendorMessage.trim(),
            resubmit,
            expedited,
            scheduledArrivalDate: carStatus === 'pre-order' && scheduledArrivalDate ? scheduledArrivalDate.toISOString() : undefined,
        }).then(async () => {
            toast({
                title: 'Quote Submitted',
                description: 'Your quote has been queued to submit.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            });
            await queryClient.invalidateQueries({ queryKey: ['/order/workflow-status', orderId] });
            onClose();
        }).catch((error) => {
            console.log(error.response.status);
            if (error?.response?.status === 422) {
                toast({
                    title: error.response.data?.message || "We are still processing this order, please try again in a few moments",
                    status: "error",
                    duration: 7500,
                    isClosable: true,
                    position: 'bottom-right'
                });
            } else if (error?.response?.status === 409) {
                toast({
                    title: error.response.data?.message || "Submit quotes already queued. Please check back shortly",
                    status: "error",
                    duration: 7500,
                    isClosable: true,
                    position: 'bottom-right'
                });
            } else {
                toast({
                    title: "An error occured",
                    status: "info",
                    duration: 5000,
                    isClosable: true,
                    position: 'bottom-right'
                });
            }
        });
    };

    const handleSkipQuoting = async () => {
        if (skipQuoteDisabled || !quoteData?.canSkipQuote || skipQuotingMarketplaces.length === 0) return;
        try {
            await skipQuotingMutation.mutateAsync({
                orderId: quoteData.orderId,
                allowAutoOrder: effectiveAllowAutoOrder,
                potentialTotal: potentialTotalLoss,
                quotesOnly,
                preOrder: carStatus === 'pre-order',
                oemEndorsement: quoteData.enforceOemEndorsement ? true : oemEndorsement,
                resubmit,
            });
            toast({
                title: 'Quoting Skipped. We are fetching quotes. Please check back shortly!',
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'bottom-right'
            });
            await queryClient.invalidateQueries({ queryKey: ['/order/workflow-status', orderId] });
            onClose();
        } catch (error: any) {
            toast({
                title: 'Error skipping quoting',
                description: error.message || 'Something went wrong.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'bottom-right'
            });
        }
    };

    if (isLoading || !quoteData) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader >Confirm Quote Submission</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Flex justify="center" align="center" h="300px">
                            <Spinner size="xl" color="blue.500" />
                        </Flex>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent maxH="90vh">
                <ModalHeader
                    borderBottomWidth={bodyOverflows ? '1px' : '0'}
                    borderBottomStyle="solid"
                    borderColor="gray.300"
                >Confirm Quote Submission</ModalHeader>
                <ModalCloseButton />
                <ModalBody ref={bodyRef} minH={0} maxH="75vh" overflowY="auto">
                    <VStack spacing={4} align="stretch">
                        {/* RO Number Section */}
                        <Box
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                        >
                            <HStack spacing={2} align="center">
                                <Text fontWeight="semibold" fontSize="md" color="gray.700">
                                    RO Number:
                                </Text>
                                {isEditingRoNumber ? (
                                    <Input
                                        value={editedRoNumber}
                                        onChange={(e) => setEditedRoNumber(e.target.value.trim())}
                                        onBlur={handleRoNumberSave}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleRoNumberSave();
                                            } else if (e.key === 'Escape') {
                                                setIsEditingRoNumber(false);
                                                setEditedRoNumber(quoteData?.roNumber?.trim() || '');
                                            }
                                        }}
                                        placeholder="Enter RO number"
                                        size="sm"
                                        width="240px"
                                        autoFocus
                                        isDisabled={updateOrderDetailsMutation.isPending}
                                        fontSize="sm"
                                        color="gray.900"
                                    />
                                ) : (
                                    <>
                                        <Text fontSize="md" color="gray.900">
                                            {editedRoNumber}
                                        </Text>
                                        <IconButton
                                            aria-label="Edit RO Number"
                                            icon={<Icon as={TbPencil as React.ElementType} w={3} h={3} />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={handleRoNumberEdit}
                                            isDisabled={updateOrderDetailsMutation.isPending}
                                            _hover={{
                                                bg: "gray.100"
                                            }}
                                            minW="auto"
                                            h="auto"
                                            p={1}
                                        />
                                    </>
                                )}
                            </HStack>
                            <Text fontSize="sm" color="gray.500" mt={1}>
                                {quoteData.locationName}
                            </Text>
                        </Box>

                        {quoteData.oemCertification && (
                            <Badge colorScheme="purple" variant="subtle" fontSize="sm" px={3} py={1} borderRadius="md">
                                OEM Certification
                            </Badge>
                        )}


                        {/* Warning for missing RO number */}
                        {editedRoNumber.length === 0 && (
                            <Alert status="error" borderRadius="lg">
                                <AlertIcon />
                                <Text fontSize="sm">
                                    <strong>Wait:</strong> No RO number has been set for this order. Please add an RO number before requesting quotes.
                                </Text>
                            </Alert>
                        )}

                        {/* Warning for RO number with no digits */}
                        {editedRoNumber.trim().length > 0 && !/\d/.test(editedRoNumber) && (
                            <Alert status="warning" borderRadius="lg">
                                <AlertIcon />
                                <Text fontSize="sm">
                                    <strong>Warning:</strong> RO number contains no numeric digits. Please update to a valid RO number.
                                </Text>
                            </Alert>
                        )}

                        {/* Intro Text Section */}
                        <Box
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                            bg="gray.50"
                        >
                            <Text color="gray.600" fontSize="sm">
                                Configure quote options and review the parts that will be quoted
                            </Text>
                        </Box>



                        {/* Purpose Section */}
                        <Box
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                        >
                            <FormLabel fontWeight="semibold" fontSize="md" mb={3}>
                                Options
                            </FormLabel>
                            <VStack align="start" spacing={3}>
                                {quoteData.canDisableAutoOrder && quoteData.orgAllowAutoOrder &&
                                    <Checkbox
                                        isChecked={effectiveAllowAutoOrder}
                                        onChange={(e) => setAllowAutoOrder(e.target.checked)}
                                        colorScheme="blue"
                                        isDisabled={autoOrderForcedOff}
                                    >
                                        Allow Auto-Order
                                    </Checkbox>}
                                {quoteData.canDisableAutoOrder && quoteData.orgAllowAutoOrder && autoOrderBlockedForCustomerPay && (
                                    <Text fontSize="xs" color="gray.500" pl={6}>
                                        Auto-order is disabled for Customer Pay/Self Pay orders in your org settings.
                                    </Text>
                                )}
                                <Checkbox
                                    isChecked={potentialTotalLoss}
                                    onChange={(e) => setPotentialTotalLoss(e.target.checked)}
                                    colorScheme="blue"
                                >
                                    <HStack spacing={1}>
                                        <Text>Potential Total</Text>
                                        <Tooltip
                                            label="Yaxle will not auto order any parts when the quoting period is done"
                                            placement="top"
                                            hasArrow
                                        >
                                            <Box as="span" display="inline-flex" tabIndex={0}>
                                                <Icon as={TbInfoCircle as React.ElementType} w={4} h={4} color="gray.500" />
                                            </Box>
                                        </Tooltip>
                                    </HStack>
                                </Checkbox>
                                <Checkbox
                                    isChecked={quotesOnly}
                                    onChange={(e) => setQuotesOnly(e.target.checked)}
                                    colorScheme="blue"
                                    isDisabled={quoteData.oemCertification}
                                >
                                    <HStack spacing={1}>
                                        <Text>Quotes Only</Text>
                                        <Tooltip
                                            label="Yaxle will not auto order any parts when the quoting period is done"
                                            placement="top"
                                            hasArrow
                                        >
                                            <Box as="span" display="inline-flex" tabIndex={0}>
                                                <Icon as={TbInfoCircle as React.ElementType} w={4} h={4} color="gray.500" />
                                            </Box>
                                        </Tooltip>
                                    </HStack>
                                </Checkbox>
                                <Checkbox
                                    isChecked={quoteData.enforceOemEndorsement ? true : oemEndorsement}
                                    onChange={(e) => setOemEndorsement(e.target.checked)}
                                    isDisabled={quoteData.enforceOemEndorsement}
                                    colorScheme="blue"
                                >
                                    <HStack spacing={2}>
                                        <Text>OEM Endorsement</Text>
                                        {quoteData.enforceOemEndorsement && (
                                            <Badge colorScheme="blue" fontSize="2xs" textTransform="uppercase">
                                                Required
                                            </Badge>
                                        )}
                                    </HStack>
                                </Checkbox>
                                {quoteData.enforceOemEndorsement && (
                                    <Box
                                        alignSelf="stretch"
                                        ml={6}
                                        bg="blue.50"
                                        border="1px solid"
                                        borderColor="blue.200"
                                        borderLeftWidth="4px"
                                        borderLeftColor="blue.500"
                                        borderRadius="md"
                                        px={3}
                                        py={2}
                                    >
                                        <HStack spacing={2} align="flex-start">
                                            <Icon
                                                as={TbInfoCircle as React.ElementType}
                                                color="blue.500"
                                                boxSize={4}
                                                mt="2px"
                                                flexShrink={0}
                                            />
                                            <Box flex={1} minW={0}>
                                                <Text fontSize="xs" fontWeight="semibold" color="blue.800" lineHeight="short">
                                                    OEM Endorsement Required
                                                </Text>
                                                <Text fontSize="xs" color="blue.700" mt={0.5} lineHeight="short">
                                                    {quoteData.enforceOemEndorsementReason || 'OEM endorsement is required for this order.'}
                                                </Text>
                                            </Box>
                                        </HStack>
                                    </Box>
                                )}
                                {quoteData.allowExpedited ? <Checkbox
                                    isChecked={expedited}
                                    onChange={(e) => setExpedited(e.target.checked)}
                                    colorScheme="blue"
                                >
                                    <HStack spacing={1}>
                                        <Text>Expedited</Text>
                                        <Tooltip
                                            label="Yaxle will prioriize delivery time over margin when selecting parts"
                                            placement="top"
                                            hasArrow
                                        >
                                            <Box as="span" display="inline-flex" tabIndex={0}>
                                                <Icon as={TbInfoCircle as React.ElementType} w={4} h={4} color="gray.500" />
                                            </Box>
                                        </Tooltip>
                                    </HStack>
                                </Checkbox> : null}
                                {quoteData.allowDealerReferral ? <Checkbox
                                    isChecked={dealerReferral}
                                    onChange={(e) => {
                                        setDealerReferral(e.target.checked);
                                        if (!e.target.checked) {
                                            setSelectedDealer(null);
                                        }
                                    }}
                                    colorScheme="blue"
                                >
                                    Dealer Referral
                                </Checkbox> : null}

                                {dealerReferral && quoteData.allowDealerReferral && (
                                    <Box w="100%" pl={6}>
                                        {selectedDealer ? (
                                            <Box
                                                p={2}
                                                bg="blue.50"
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor="blue.200"
                                            >
                                                <HStack justify="space-between">
                                                    <Text fontSize="sm" fontWeight="medium" color="blue.700">
                                                        Selected: {selectedDealer.title}
                                                    </Text>
                                                    <IconButton
                                                        icon={<Icon as={TbPencil as React.ElementType} />}
                                                        aria-label="Change supplier"
                                                        size="xs"
                                                        variant="ghost"
                                                        colorScheme="blue"
                                                            onClick={() => {
                                                                setSelectedDealer(null);
                                                            }}
                                                    />
                                                </HStack>
                                            </Box>
                                        ) : (
                                            <SupplierSearchField
                                                placeholder="Search for a supplier..."
                                                emptyMessage="No suppliers found"
                                                onSelect={(supplier) => {
                                                    setSelectedDealer({
                                                        _id: supplier._id,
                                                        title: supplier.title,
                                                    });
                                                }}
                                            />
                                        )}
                                    </Box>
                                )}
                            </VStack>
                        </Box>

                        {/* Car Status Section */}
                        <Box
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                        >
                            <FormControl isRequired>
                                <FormLabel fontWeight="semibold" fontSize="md" mb={3}>
                                    Car Status
                                </FormLabel>
                                <RadioGroup value={carStatus} onChange={(val) => {
                                    setCarStatus(val);
                                    if (val !== 'pre-order') {
                                        setScheduledArrivalDate(null);
                                        setWantsSchedule('');
                                        setShowDatePicker(false);
                                    }
                                }}>
                                    <VStack align="start" spacing={3}>
                                        <Radio value="pre-order" colorScheme="blue">
                                            Pre-order
                                        </Radio>
                                        {carStatus === 'pre-order' && quoteData.allowScheduled && (
                                            <Box pl={6} w="100%">
                                                <VStack align="stretch" spacing={3}>
                                                    <HStack spacing={1}>
                                                        <Text fontSize="sm" color="gray.600">
                                                            Would you like to schedule parts to arrive by a specific date?
                                                        </Text>
                                                        <Tooltip
                                                            label="Scheduling an order will order parts to the best of our ability to arrive at this date. You cannot select a date sooner than your organization's onsite delivery preference"
                                                            placement="top"
                                                            hasArrow
                                                        >
                                                            <Box as="span" display="inline-flex" tabIndex={0}>
                                                                <Icon as={TbInfoCircle as React.ElementType} w={4} h={4} color="gray.500" />
                                                            </Box>
                                                        </Tooltip>
                                                    </HStack>
                                                    <RadioGroup value={wantsSchedule} onChange={(val) => {
                                                        setWantsSchedule(val as 'yes' | 'no');
                                                        if (val === 'no') {
                                                            setScheduledArrivalDate(null);
                                                            setShowDatePicker(false);
                                                        }
                                                    }}>
                                                        <HStack spacing={4}>
                                                            <Radio value="yes" colorScheme="blue" size="sm">Yes</Radio>
                                                            <Radio value="no" colorScheme="blue" size="sm">No</Radio>
                                                        </HStack>
                                                    </RadioGroup>
                                                    {wantsSchedule === 'yes' && (
                                                        <Box position="relative">
                                                            <HStack spacing={2}>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    borderColor="gray.300"
                                                                    leftIcon={<Icon as={TbCalendar as React.ElementType} w={4} h={4} />}
                                                                    onClick={() => setShowDatePicker(!showDatePicker)}
                                                                    fontWeight="normal"
                                                                    color={scheduledArrivalDate ? 'gray.800' : 'gray.500'}
                                                                >
                                                                    {scheduledArrivalDate ? formatScheduledDate(scheduledArrivalDate) : 'Select ideal ETA date'}
                                                                </Button>
                                                                {scheduledArrivalDate && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        color="gray.400"
                                                                        _hover={{ color: 'gray.600' }}
                                                                        onClick={() => {
                                                                            setScheduledArrivalDate(null);
                                                                            setShowDatePicker(false);
                                                                        }}
                                                                        px={2}
                                                                        minW="auto"
                                                                    >
                                                                        Clear
                                                                    </Button>
                                                                )}
                                                            </HStack>
                                                            {showDatePicker && (
                                                                <>
                                                                    <Box
                                                                        position="fixed"
                                                                        top={0}
                                                                        left={0}
                                                                        right={0}
                                                                        bottom={0}
                                                                        zIndex={1}
                                                                        onClick={() => setShowDatePicker(false)}
                                                                    />
                                                                    <Box
                                                                        position="absolute"
                                                                        zIndex={2}
                                                                        top="100%"
                                                                        left={0}
                                                                        mt={1}
                                                                        border="1px solid"
                                                                        borderColor="gray.200"
                                                                        borderRadius="lg"
                                                                        p={3}
                                                                        bg="white"
                                                                        boxShadow="lg"
                                                                        w="280px"
                                                                    >
                                                                        <SingleDateCalendar
                                                                            selectedDate={scheduledArrivalDate}
                                                                            minDate={addBusinessHours(new Date(), quoteData.preferredDeliveryTimePreference)}
                                                                            onChange={(date) => {
                                                                                setScheduledArrivalDate(date);
                                                                                setShowDatePicker(false);
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </>
                                                            )}
                                                            {scheduledArrivalDate && (
                                                                <Text fontSize="xs" color="gray.500" mt={1}>
                                                                    Parts will be scheduled to arrive on-site by {formatScheduledDate(scheduledArrivalDate)}
                                                                </Text>
                                                            )}
                                                        </Box>
                                                    )}
                                                </VStack>
                                            </Box>
                                        )}
                                        <Radio value="on-site" colorScheme="blue">
                                            On site
                                        </Radio>
                                    </VStack>
                                </RadioGroup>
                            </FormControl>
                        </Box>

                        {/* Welded-on Radiator Support and/or VIN-Coded Headlamps Section */}
                        {quoteData?.needsPartTypeVerification && (
                            <Box
                                p={4}
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="lg"
                            >
                                <VStack align="start" spacing={0} mb={3}>
                                    <Text fontWeight="semibold" fontSize="md">
                                        Are there any parts with special circumstances that require a flag? For example a welded-on radiator support or head/tail lamps with logos.
                                    </Text>
                                </VStack>
                                <RadioGroup value={hasWeldedOrVinCoded} onChange={(val) => setHasWeldedOrVinCoded(val as 'yes' | 'no' | '')}>
                                    <HStack spacing={6}>
                                        <Radio value="yes" colorScheme="blue">
                                            Yes
                                        </Radio>
                                        <Radio value="no" colorScheme="blue">
                                            No
                                        </Radio>
                                    </HStack>
                                </RadioGroup>
                                {hasWeldedOrVinCoded === 'yes' && !quoteData?.parts.some(part => part.manualFlag) && (
                                    <Alert status="error" borderRadius="lg" mt={3}>
                                        <AlertIcon />
                                        <Text fontSize="sm">
                                            You must flag the part(s) in question before requesting parts
                                        </Text>
                                    </Alert>
                                )}
                            </Box>
                        )}

                        {/* Flagged Parts Section */}
                        {quoteData?.parts.some(part => part.manualFlag) && (
                            <Box
                                p={4}
                                border="1px solid"
                                borderColor="orange.200"
                                borderRadius="lg"
                                bg="orange.50"
                            >
                                <FormLabel fontWeight="semibold" fontSize="md" mb={3} color="orange.800">
                                    Flagged Parts
                                </FormLabel>
                                <VStack spacing={4} align="stretch">
                                    {quoteData.parts
                                        .filter(part => part.manualFlag)
                                        .map((part) => (
                                            <Box
                                                key={part._id}
                                                p={3}
                                                border="1px solid"
                                                borderColor="orange.200"
                                                borderRadius="md"
                                                bg="white"
                                            >
                                                <VStack spacing={2} align="stretch">
                                                    <Text fontSize="sm" fontWeight="bold" color="gray.900">
                                                        {part.title}
                                                    </Text>
                                                    <HStack spacing={4} align="center">
                                                        <Text fontSize="xs" color="gray.600" fontFamily="mono">
                                                            OE #: {part.partNumber}
                                                        </Text>
                                                        <PartType partType={part.emsPartType} />
                                                    </HStack>
                                                </VStack>
                                            </Box>
                                        ))}
                                </VStack>
                            </Box>
                        )}

                        {/* Parts to be Excluded */}
                        <Box
                            p={4}
                            border="1px solid"
                            borderColor={quoteData?.parts.some(part => part.excludeQuoting) ? "red.200" : "gray.200"}
                            borderRadius="lg"
                            bg={quoteData?.parts.some(part => part.excludeQuoting) ? "red.50" : "white"}
                        >
                            <FormLabel fontWeight="semibold" fontSize="md" mb={3} color={quoteData?.parts.some(part => part.excludeQuoting) ? "red.800" : "gray.700"}>
                                Parts to be Excluded
                            </FormLabel>
                            <VStack spacing={4} align="stretch">
                                {quoteData?.parts
                                    .filter(part => part.excludeQuoting)
                                    .map((part, index) => (
                                        <Box
                                            key={part._id}
                                            p={3}
                                            border="1px solid"
                                            borderColor="red.200"
                                            borderRadius="md"
                                            bg="white"
                                        >
                                            <VStack spacing={2} align="stretch">
                                                <Text fontSize="sm" fontWeight="bold" color="gray.900">
                                                    {part.title}
                                                </Text>
                                                <HStack spacing={4} align="center">
                                                    <Text fontSize="xs" color="gray.600" fontFamily="mono">
                                                        OE #: {part.partNumber}
                                                    </Text>
                                                    <PartType partType={part.emsPartType} />
                                                </HStack>
                                            </VStack>
                                        </Box>
                                    ))}
                                {quoteData && quoteData.parts.filter(part => part.excludeQuoting).length === 0 && (
                                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                                        No parts excluded
                                    </Text>
                                )}
                            </VStack>
                        </Box>

                        {/* Vendor Message Section */}
                        <Box
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                        >
                            <FormLabel fontWeight="semibold" fontSize="md" mb={1} pb={3}>
                                Vendor Message
                            </FormLabel>

                            {showQuickSelect && (
                                <Box mb={3}>
                                    <Wrap spacing={2}>
                                        {VENDOR_MESSAGE_QUICK_SELECT.map((message, index) => (
                                            <WrapItem key={index}>
                                                <Tag
                                                    size="md"
                                                    px={4}
                                                    py={2.5}
                                                    variant="subtle"
                                                    colorScheme="blue"
                                                    textTransform="none"
                                                    fontWeight="medium"
                                                    letterSpacing="normal"
                                                    cursor="pointer"
                                                    borderRadius="lg"
                                                    _hover={{ bg: "blue.100", color: "blue.800" }}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleQuickSelect(message);
                                                    }}
                                                    transition="all 0.2s"
                                                >
                                                    {message}
                                                </Tag>
                                            </WrapItem>
                                        ))}
                                    </Wrap>
                                </Box>
                            )}

                            <Textarea
                                placeholder="Enter an optional message to send to vendors..."
                                value={vendorMessage}
                                onChange={(e) => setVendorMessage(e.target.value)}
                                onFocus={() => setShowQuickSelect(true)}
                                onBlur={() => setShowQuickSelect(false)}
                                size="sm"
                                rows={4}
                                resize="vertical"
                            />
                            <Text fontSize="xs" color="gray.400" mt={1.5}>
                                Optional. This message will be sent to vendors.
                            </Text>
                        </Box>

                        {(quoteData.marketplaces ?? []).length > 0 && (
                            <Box
                                p={4}
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="lg"
                            >
                                <FormLabel fontWeight="semibold" fontSize="md" mb={3}>
                                    Marketplaces
                                </FormLabel>
                                <Wrap spacing={3}>
                                    {quoteData.marketplaces.map((marketplace) => (
                                        <WrapItem key={marketplace}>
                                            <HStack spacing={2}>
                                                <SourceBadge source={marketplace} />
                                                <Text fontSize="sm" color="gray.800">
                                                    {getSourceLabel(marketplace)}
                                                </Text>
                                            </HStack>
                                        </WrapItem>
                                    ))}
                                </Wrap>
                            </Box>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <HStack w="100%" justify="space-between">
                        <Button size="action" variant="outlineGray" onClick={onClose}>
                            Cancel
                        </Button>
                        <HStack spacing={3}>
                            {quoteData?.canSkipQuote && skipQuotingMarketplaces.length > 0 && (
                                <Popover placement="top" isLazy>
                                    {({ onClose: onPopoverClose }) => (
                                        <>
                                            <PopoverTrigger>
                                                <Button
                                                    size="action"
                                                    variant="outlineGray"
                                                    isDisabled={skipQuoteDisabled}
                                                    isLoading={skipQuotingMutation.isPending}
                                                    loadingText="Skipping..."
                                                >
                                                    Skip Quoting
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent w="320px">
                                                <PopoverArrow />
                                                <PopoverBody>
                                                    <Text fontSize="sm" color="gray.700">
                                                        Are you sure you want to skip quoting? We will directly get quotes from {formatSkipQuotingSources(quoteData.marketplaces)}.
                                                    </Text>
                                                </PopoverBody>
                                                <PopoverFooter border="0" pt={0} display="flex" justifyContent="flex-end" gap={2}>
                                                    <Button size="sm" variant="ghost" onClick={onPopoverClose}>
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        bg="#4051B5"
                                                        color="white"
                                                        _hover={{ bg: '#3041A5' }}
                                                        onClick={() => {
                                                            onPopoverClose();
                                                            void handleSkipQuoting();
                                                        }}
                                                        isLoading={skipQuotingMutation.isPending}
                                                    >
                                                        Skip Quoting
                                                    </Button>
                                                </PopoverFooter>
                                            </PopoverContent>
                                        </>
                                    )}
                                </Popover>
                            )}
                            <Button
                                size="action"
                                bg="#4051B5"
                                color="white"
                                _hover={{ bg: "#3041A5" }}
                                onClick={handleSubmitQuote}
                                isLoading={requestQuotesMutation.isPending}
                                loadingText="Submitting..."
                                isDisabled={areQuoteActionsDisabled}
                            >
                                Request Parts
                            </Button>
                        </HStack>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SubmitQuoteModal;
